import React, { useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactQuill from 'react-quill-new';
import { DEFAULT_TOOLBAR_CONFIG } from '@/config/quillConfig';
import { buildQuillModules } from '@/utils/buildQuillModules';
import 'react-quill-new/dist/quill.snow.css';
import { uploadFile } from '@/services/common.service.ts';
import { commonService } from '@/services/common.service.ts';
import { API_UPLOAD_URL } from '@/constants';
import type { QuillToolbarConfig } from '@/config/quillTypes.ts';
interface TextareaProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
  rows?: number;
  maxLength?: number;
  showCount?: boolean;
  typeFile?: string;
  isPasteEnabled?: boolean;
  onImageUploaded?: (base64: string, path: string) => void;
  toolbarConfig?: QuillToolbarConfig;
  onImageError?: (error: string | null) => void;
}

const Textarea = forwardRef<ReactQuill, TextareaProps>(
  (
    {
      label,
      placeholder,
      value = '',
      onChange,
      disabled = false,
      error,
      required = false,
      className = '',
      maxLength,
      showCount = false,
      typeFile = '0',
      onImageUploaded,
      isPasteEnabled = false,
      toolbarConfig = DEFAULT_TOOLBAR_CONFIG,
      onImageError,
    },
    ref
  ) => {
    const quillRef = useRef<ReactQuill>(null);

    // Expose Quill instance to parent
    useImperativeHandle(ref, () => quillRef.current as any);

    // Lưu mapping base64 <-> { path, fileUuid }
    const imageMapRef = useRef<Record<string, { path: string; fileUuid: string }>>({});
    const insertImageToEditor = async (file: File) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        if (typeof onImageError === 'function') onImageError('jpg / png / jpeg 形式の画像を選択してください。');
        return;
      }
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        if (typeof onImageError === 'function') onImageError('5MB未満の画像をアップロードしてください。');
        return;
      }
      if (typeof onImageError === 'function') onImageError(null);

      // 1. Đọc file thành base64 để preview ngay
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target?.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

      // 2. Chèn ảnh base64 vào editor
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      quill.insertEmbed(index, 'image', base64, 'user');
      quill.setSelection(index + 1, 0, 'silent');

      // 3. Upload file lên server
      try {
        const imageUrl = await uploadFile('/admin/general/file/upload', file, typeFile);
        const path = API_UPLOAD_URL + imageUrl.data.path;
        const fileUuid = imageUrl.data.fileUuid;

        // Lưu mapping base64 <-> { path, fileUuid }
        imageMapRef.current[base64] = { path, fileUuid };

        // Gọi callback để cha lưu mapping base64 <-> path
        if (onImageUploaded) {
          onImageUploaded(base64, path);
        }
      } catch (err) {
        console.error('Image upload failed:', err);
      }
    };

    /**
     * Handle image upload
     * This function is called when user clicks the image button in toolbar
     */
    const handleImageUpload = () => {
      // Create file input element
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', '.jpg,.jpeg,.png');

      input.addEventListener('change', async () => {
        const file = input.files?.[0];
        if (file) {
          await insertImageToEditor(file);
        }
      });

      input.click();
    };

    // Build modules with handlers
    const modules = useMemo(() => {
      const baseModules = buildQuillModules(toolbarConfig);

      // toolbar.container is the array of toolbar items
      // toolbar.handlers is the object of custom handlers
      return {
        ...baseModules,
        toolbar: {
          container: baseModules.toolbar,
          handlers: {
            image: handleImageUpload,
          },
        },
      };
    }, []);

    // Handle paste and drop events
    useEffect(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const editorElement = quill.root;

      /**
       * Handle paste event to block images from clipboard
       */
      const handlePaste = (e: ClipboardEvent) => {
        const clipboardData = e.clipboardData;
        if (!clipboardData) return;

        const hasImage = Array.from(clipboardData.items).some(
          item => item.kind === 'file' && item.type.startsWith('image/')
        );

        if (isPasteEnabled) {
          // Only block image pasting, allow text
          if (hasImage) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          // Allow text paste
        } else {
          // Block all pasting
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      };

      /**
       * Handle drop event to block dragged images
       */
      const handleDrop = (e: DragEvent) => {
        const files = e.dataTransfer?.files;
        if (files && Array.from(files).some(file => file.type.startsWith('image/'))) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Prevent default drop behavior to avoid Quill inserting base64 automatically
        e.preventDefault();
        e.stopPropagation();

        // Find first image file
        const imageFile = Array.from(files).find(file => file.type.startsWith('image/'));

        if (!imageFile) {
          console.log('No image found in dropped files');
          return;
        }

        console.log('Image file found in drop:', imageFile.type);
        void insertImageToEditor(imageFile);
      };

      /**
       * Handle dragover event to allow dropping
       */
      const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
      };

      // Always add paste handler, logic inside handler depends on isPasteEnabled
      editorElement.addEventListener('paste', handlePaste as EventListener, true);
      editorElement.addEventListener('drop', handleDrop as EventListener, true);
      editorElement.addEventListener('dragover', handleDragOver as EventListener, true);

      // Cleanup
      return () => {
        editorElement.removeEventListener('paste', handlePaste as EventListener, true);
        editorElement.removeEventListener('drop', handleDrop as EventListener, true);
        editorElement.removeEventListener('dragover', handleDragOver as EventListener, true);
      };
    }, [isPasteEnabled]);

    // Xử lý sự kiện xóa ảnh trong editor
    useEffect(() => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      let prevImages: string[] = [];

      // Lấy danh sách src ảnh hiện tại trong editor
      const getCurrentImages = () => {
        const imgs = Array.from(quill.root.querySelectorAll('img')) as HTMLImageElement[];
        return imgs.map(img => img.src);
      };

      // Khởi tạo danh sách ảnh ban đầu
      prevImages = getCurrentImages();

      const handleTextChange = () => {
        const currentImages = getCurrentImages();
        // Ảnh bị xóa là những ảnh có trong prevImages nhưng không còn trong currentImages
        const deletedImages = prevImages.filter(src => !currentImages.includes(src));
        deletedImages.forEach(async src => {
          const imageInfo = imageMapRef.current[src];
          if (imageInfo) {
            // Log fileUuid khi xóa ảnh
            if (imageInfo.fileUuid) {
              try {
                await commonService.deleteFileUpload(imageInfo.fileUuid);
              } catch (error) {
                console.error('Failed to delete image file:', error);
              }
              // Xóa luôn ở đây (gọi API xóa trực tiếp)
              // import { deleteFile } from '@/services/common.service.ts'; ở đầu file nếu chưa có
              // await deleteFile(imageInfo.fileUuid);
              // Nếu deleteFile là async, cần sửa handleTextChange thành async và dùng quill.on('text-change', async ...)
            }
            // Xóa mapping
            delete imageMapRef.current[src];
          }
        });
        prevImages = currentImages;
      };

      quill.on('text-change', handleTextChange);

      return () => {
        quill.off('text-change', handleTextChange);
      };
    }, []);

    const handleChange = (content: string) => {
      onChange?.(content);
    };

    // Calculate character count (strip HTML tags)
    const getTextLength = (html: string) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent?.length || 0;
    };

    return (
      <div className={`form-field ${className}`}>
        {label && (
          <label className="form-label text-[14px] leading-tight">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div
          className={`quill-wrapper ${error ? 'quill-error' : ''} ${disabled ? 'quill-disabled' : ''}`}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={handleChange}
            readOnly={disabled}
            placeholder={placeholder}
            modules={modules}
            className="quill-editor-content"
          />
          {showCount && maxLength && (
            <span className="form-count">
              {getTextLength(value)}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }
);

export default Textarea;
